import { useState } from "react";
import { useCustom, useCustomMutation } from "@refinedev/core";
import { Pin, Trash2, Edit2, Send, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";

interface Note {
  id: number;
  orderId: number;
  adminUserId: number;
  content: string;
  isPinned: boolean;
  createdAt: string;
  updatedAt: string;
  adminUser?: {
    id: number;
    username: string;
  } | null;
}

interface OrderNotesProps {
  orderId: number;
}

export function OrderNotes({ orderId }: OrderNotesProps) {
  const [newNote, setNewNote] = useState("");
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editContent, setEditContent] = useState("");

  const notesQuery = useCustom<Note[]>({
    url: `/orders/${orderId}/notes`,
    method: "get",
  });

  const isLoading = notesQuery.query?.isLoading ?? false;
  const refetch = notesQuery.query?.refetch;
  const notes = notesQuery.query?.data?.data ?? [];

  const createMutation = useCustomMutation();
  const createNote = createMutation.mutate;
  const isCreating = createMutation.mutation.isPending;

  const updateMutation = useCustomMutation();
  const updateNote = updateMutation.mutate;
  const isUpdating = updateMutation.mutation.isPending;

  const deleteMutation = useCustomMutation();
  const deleteNote = deleteMutation.mutate;
  const isDeleting = deleteMutation.mutation.isPending;

  const toggleMutation = useCustomMutation();
  const togglePin = toggleMutation.mutate;
  const isToggling = toggleMutation.mutation.isPending;

  const formatDate = (value: string) =>
    new Date(value).toLocaleDateString("ru-RU", {
      day: "2-digit",
      month: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });

  const handleCreate = () => {
    if (!newNote.trim()) {return;}
    createNote(
      {
        url: `/orders/${orderId}/notes`,
        method: "post",
        values: { content: newNote },
      },
      {
        onSuccess: () => {
          setNewNote("");
          refetch?.();
        },
      }
    );
  };

  const handleUpdate = (noteId: number) => {
    if (!editContent.trim()) {return;}
    updateNote(
      {
        url: `/orders/${orderId}/notes/${noteId}`,
        method: "put",
        values: { content: editContent },
      },
      {
        onSuccess: () => {
          setEditingId(null);
          setEditContent("");
          refetch?.();
        },
      }
    );
  };

  const handleDelete = (noteId: number) => {
    deleteNote(
      {
        url: `/orders/${orderId}/notes/${noteId}`,
        method: "delete",
        values: {},
      },
      {
        onSuccess: () => refetch?.(),
      }
    );
  };

  const handleTogglePin = (noteId: number) => {
    togglePin(
      {
        url: `/orders/${orderId}/notes/${noteId}/pin`,
        method: "patch",
        values: {},
      },
      {
        onSuccess: () => refetch?.(),
      }
    );
  };

  const startEdit = (note: Note) => {
    setEditingId(note.id);
    setEditContent(note.content);
  };

  if (isLoading) {
    return (
      <div className="space-y-2">
        {[1, 2, 3].map((i) => (
          <Skeleton key={i} className="h-12 w-full" />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {/* Add note form */}
      <div className="flex gap-1.5">
        <Textarea
          placeholder="Добавить заметку..."
          value={newNote}
          onChange={(e) => setNewNote(e.target.value)}
          className="min-h-[56px] resize-none text-sm"
        />
        <Button
          size="icon"
          className="h-8 w-8 shrink-0 self-start"
          onClick={handleCreate}
          disabled={!newNote.trim() || isCreating}
        >
          {isCreating ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <Send className="h-3.5 w-3.5" />
          )}
        </Button>
      </div>

      {/* Notes list */}
      <ScrollArea className="h-[280px]">
        <div className="space-y-1.5 pr-3">
          {notes.length === 0 && (
            <div className="text-center text-muted-foreground py-4 text-sm">
              Нет заметок
            </div>
          )}

          {notes.map((note) => (
            <div
              key={note.id}
              className={cn(
                "rounded border px-2.5 py-1.5 space-y-1",
                note.isPinned && "border-primary bg-primary/5"
              )}
            >
              {/* Header */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-1.5">
                  <span className="text-xs font-medium">
                    {note.adminUser?.username ?? "Неизвестно"}
                  </span>
                  <span className="text-[11px] text-muted-foreground">
                    {formatDate(note.createdAt)}
                  </span>
                  {note.isPinned && (
                    <Pin className="h-2.5 w-2.5 text-primary fill-primary" />
                  )}
                </div>
                <div className="flex items-center gap-0.5">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6"
                    onClick={() => handleTogglePin(note.id)}
                    disabled={isToggling}
                  >
                    <Pin
                      className={cn(
                        "h-2.5 w-2.5",
                        note.isPinned && "fill-current"
                      )}
                    />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6"
                    onClick={() => startEdit(note)}
                  >
                    <Edit2 className="h-2.5 w-2.5" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6 text-destructive hover:text-destructive"
                    onClick={() => handleDelete(note.id)}
                    disabled={isDeleting}
                  >
                    <Trash2 className="h-2.5 w-2.5" />
                  </Button>
                </div>
              </div>

              {/* Content */}
              {editingId === note.id ? (
                <div className="flex gap-1.5">
                  <Textarea
                    value={editContent}
                    onChange={(e) => setEditContent(e.target.value)}
                    className="min-h-[48px] resize-none text-sm"
                  />
                  <div className="flex flex-col gap-0.5">
                    <Button
                      size="sm"
                      className="h-7 text-xs"
                      onClick={() => handleUpdate(note.id)}
                      disabled={isUpdating}
                    >
                      {isUpdating ? (
                        <Loader2 className="h-3 w-3 animate-spin" />
                      ) : (
                        "OK"
                      )}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-7 text-xs"
                      onClick={() => setEditingId(null)}
                    >
                      Нет
                    </Button>
                  </div>
                </div>
              ) : (
                <p className="text-xs whitespace-pre-wrap">{note.content}</p>
              )}
            </div>
          ))}
        </div>
      </ScrollArea>
    </div>
  );
}
